import { useTranslation } from 'react-i18next';
import {
    formatFacilityDisplayDate,
    getDaysRemaining,
    type Facility,
} from '../utils/utils';

interface OnboardingTrialHeaderProps {
    facility: Facility;
    showDescription?: boolean;
}

export default function OnboardingTrialHeader({
    facility,
    showDescription = true,
}: OnboardingTrialHeaderProps) {
    const { t } = useTranslation();

    if (!facility.start_date) return null;

    const goLiveDays = getDaysRemaining(facility.start_date, 'go-live');
    const countdown =
        goLiveDays === null
            ? null
            : goLiveDays > 0
            ? t('facilities.onboarding.trialHeader.startsIn', { days: goLiveDays })
            : goLiveDays === 0
            ? t('facilities.onboarding.trialHeader.startsToday')
            : t('facilities.onboarding.trialHeader.startedAgo', { days: Math.abs(goLiveDays) });

    return (
        <div className="mb-3">
            <div className="flex items-center gap-3">
                <h3 className="text-xl font-semibold text-foreground">
                    {facility.isTrial ? (
                        <>
                            {t('facilities.onboarding.trialHeader.trial')}{' '}
                            {formatFacilityDisplayDate(facility.start_date)}
                            {facility.end_date && (
                                <> — {formatFacilityDisplayDate(facility.end_date)}</>
                            )}
                        </>
                    ) : (
                        t('facilities.onboarding.trialHeader.goesLive', {
                            date: formatFacilityDisplayDate(facility.start_date),
                        })
                    )}
                </h3>
                {countdown && (
                    <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-500">
                        {countdown}
                    </span>
                )}
            </div>
            {showDescription && (
                <p className="mt-1 text-sm text-muted-foreground">
                    {t(
                        facility.isTrial
                            ? 'facilities.onboarding.trialHeader.completeSteps'
                            : 'facilities.onboarding.trialHeader.completeStepsSubscription'
                    )}
                </p>
            )}
        </div>
    );
}
